import * as React from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import TextField from '@material-ui/core/TextField'
import { IPhoto } from '../../models/Checklist'
import {
  IImageStorage,
  isLocalImage,
  TEMP_IMAGE_PREFIX
} from '../../data_sources/imagestorage/imageStorage'
import { Card, CardContent, DialogActions, Button } from '@material-ui/core'
import TabsComponent, { ITabDefinition } from '../common/Tabs'
import { PhotoComments } from './PhotoComments'
import { formatDate } from '../../utils/dates'
import {
  withOneplaceLibraryContext,
  IOneplaceLibraryContextProp
} from '../OneplaceLibraryProvider'
import PhotoAnnotationEditor from './PhotoAnnotationEditor'
import { blobToBase64, base64ToBlob } from './utils/photoAnnotationUtils'
import { replaceLocalPhotoData } from './utils/processPhoto'

const MIN_PHOTO_HEIGHT = 200

// this is the reserved padding for the comment section and buttons
const PHOTO_BOTTOM_SECTION_HEIGHT = 270

function getMaxPhotoHeight(): number {
  const viewportHeight = window.innerHeight

  const allowedPhotoHeight = viewportHeight - PHOTO_BOTTOM_SECTION_HEIGHT

  return Math.max(allowedPhotoHeight, MIN_PHOTO_HEIGHT)
}

export interface IPhotoDialogProps extends IOneplaceLibraryContextProp {
  mode: 'new' | 'edit'
  photo: IPhoto
  fullName?: string
  dateTimeFormat?: string
  imageStorage: IImageStorage
  isOpen: boolean
  isMultipleComment?: boolean // allow adding multiple comments?
  onClose: () => void
  onSave: (photo: IPhoto, takeAnotherPhoto: boolean) => void
  onDelete: (photo: IPhoto) => void
  allowSavedPhotoDeletion?: boolean
  allowSavedPhotoCommentUpdate?: boolean
  showTakePhotoButton?: boolean
  dialogMaxWidth?: number
}

export interface IPhotoDialogState {
  // avoid double clicking on the save button
  isClosing: boolean
  comment: string
  // save the dialog dimmesion after loading the image, so we can display the same size for comments tab
  dialogHeight: number
  dialogWidth: number
  dialogMaxWidth?: number
  isAnnotating: boolean
  annotationBase64: string | null
  annotationOriginalBase64: string | null
  annotationWidth: number | null
  videoBlobUrl: string | null
}

export const PhotoDialog = withOneplaceLibraryContext(
  class extends React.Component<IPhotoDialogProps, IPhotoDialogState> {
    private readonly photoPreviewRef: React.RefObject<HTMLImageElement>
    public static defaultProps = {
      allowSavedPhotoDeletion: false,
      showTakePhotoButton: true
    }
    static displayName = 'PhotoDialog'
    constructor(props: any) {
      super(props)
      this.photoPreviewRef = React.createRef()
      const firstComment =
        this.props.photo.comments &&
        this.props.photo.comments.filter((c1) => c1.action != 'delete').length >
          0
          ? this.props.photo.comments.filter((c1) => c1.action != 'delete')[0]!
              .comment!
          : ''
      this.state = {
        comment: firstComment || this.props.photo.comment,
        isClosing: false,
        dialogHeight: 0,
        dialogWidth: 0,
        dialogMaxWidth: 700,
        isAnnotating: false,
        annotationBase64: null,
        annotationOriginalBase64: null,
        annotationWidth: null,
        videoBlobUrl: null
      }
      this.onCommentChanged = this.onCommentChanged.bind(this)
      this.onSave = this.onSave.bind(this)
      this.onDelete = this.onDelete.bind(this)
      this.onStartAnnotation = this.onStartAnnotation.bind(this)
      this.onAnnotationSave = this.onAnnotationSave.bind(this)
      this.onAnnotationCancel = this.onAnnotationCancel.bind(this)
    }

    componentDidMount(): void {
      const photo = this.props.photo
      const isVideo = photo.contentType?.startsWith('video/')
      const isLocal = isLocalImage(photo.url)
      if (isVideo && !isLocal) {
        const url =
          this.props.ctx.client.getAuthenticatedPhotoUrl(
            photo.localObjectUrl || photo.url
          ) + '&attachment=true'
        fetch(url)
          .then((r) => r.blob())
          .then((blob) => {
            this.setState({ videoBlobUrl: URL.createObjectURL(blob) })
          })
          .catch((err) => {
            console.error('[PhotoDialog] video fetch failed:', err)
          })
      }
    }

    componentWillUnmount(): void {
      if (this.state.videoBlobUrl) {
        URL.revokeObjectURL(this.state.videoBlobUrl)
      }
    }

    async onStartAnnotation(): Promise<void> {
      const photo = this.props.photo
      const dialog = document.getElementById('photoDialog')
      const dialogHeight =
        dialog && dialog.clientHeight > 0
          ? dialog.clientHeight
          : this.state.dialogHeight
      const dialogWidth =
        dialog && dialog.clientWidth > 0
          ? dialog.clientWidth
          : this.state.dialogWidth
      const annotationWidth =
        this.photoPreviewRef.current && this.photoPreviewRef.current.clientWidth
          ? this.photoPreviewRef.current.clientWidth
          : null
      let blob: Blob
      if (isLocalImage(photo.url) && photo.localObjectUrl) {
        const response = await fetch(photo.localObjectUrl)
        blob = await response.blob()
      } else {
        const url = this.props.ctx.client.getAuthenticatedPhotoUrl(
          photo.localObjectUrl || photo.url
        )
        const response = await fetch(url)
        blob = await response.blob()
      }
      const base64 = await blobToBase64(blob)
      let originalBase64: string | null = null
      if (photo.originalUrl && isLocalImage(photo.originalUrl)) {
        try {
          const originalObjectUrl =
            await this.props.imageStorage.getImageObjectUrl(photo.originalUrl)
          const originalResponse = await fetch(originalObjectUrl)
          const originalBlob = await originalResponse.blob()
          originalBase64 = await blobToBase64(originalBlob)
        } catch (err) {
          console.error('Failed to load original photo for annotation', err)
        }
      }
      this.setState({
        isAnnotating: true,
        annotationBase64: base64,
        annotationOriginalBase64: originalBase64,
        annotationWidth,
        dialogHeight,
        dialogWidth
      })
    }

    async onAnnotationSave(annotatedBase64: string): Promise<void> {
      await replaceLocalPhotoData(
        this.props.photo,
        base64ToBlob(annotatedBase64),
        this.props.imageStorage
      )
      this.setState({
        isAnnotating: false,
        annotationBase64: null,
        annotationOriginalBase64: null,
        annotationWidth: null
      })
    }

    onAnnotationCancel(): void {
      this.setState({
        isAnnotating: false,
        annotationBase64: null,
        annotationOriginalBase64: null,
        annotationWidth: null
      })
    }

    onCommentChanged(event: any): void {
      this.setState({ comment: event.target.value })
    }
    //  async is in the setState
    // eslint-disable-next-line @typescript-eslint/require-await
    async onSave(takeAnotherPhoto: boolean): Promise<string> {
      // avoid double clicking on the save button
      if (!this.state.isClosing) {
        this.setState(
          { isClosing: true },
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          async (): Promise<void> => {
            this.props.photo.comment = this.state.comment
            if (this.state.comment) {
              this.props.photo.comments = []
              this.props.photo.comments.push({
                comment: this.state.comment,
                // name : this.props.ctx ? this.props.ctx.auth.user.fullName : ' ',
                name: this.props.fullName ? this.props.fullName : 'unknown',
                id: null,
                photoId: this.props.photo.id ? this.props.photo.id : null,
                // tslint:disable-next-line:max-line-length
                createdDateTime: formatDate(
                  'display_datetime',
                  new Date(),
                  this.props.dateTimeFormat
                    ? this.props.dateTimeFormat
                    : 'yyyy-MM-dd'
                )
              }) // added the conditional test for jest
              // should be formatDate('utc', new Date()) ;
            }
            const photo = this.props.photo
            const urlNeedsPersist = !!photo.url?.startsWith(TEMP_IMAGE_PREFIX)
            const thumbnailNeedsPersist =
              !!photo.thumbnailUrl?.startsWith(TEMP_IMAGE_PREFIX)
            const needsPersist = urlNeedsPersist || thumbnailNeedsPersist
            if (this.props.mode == 'new' || needsPersist) {
              // tslint:disable-next-line:max-line-length
              const [storedUrl, storedThumbnailUrl] = await Promise.all([
                urlNeedsPersist
                  ? this.props.imageStorage.makePermenant(photo.url)
                  : Promise.resolve(photo.url),
                thumbnailNeedsPersist
                  ? this.props.imageStorage.makePermenant(photo.thumbnailUrl)
                  : Promise.resolve(photo.thumbnailUrl)
              ])
              photo.url = storedUrl
              photo.thumbnailUrl = storedThumbnailUrl
              if (this.props.ctx.env.os == 'windows') {
                const imgObjectUrl =
                  await this.props.imageStorage.getImageObjectUrl(
                    photo.url,
                    true
                  )
                const imgThumbnailObjectUrl =
                  await this.props.imageStorage.getImageObjectUrl(
                    photo.thumbnailUrl,
                    true
                  )
                photo.localObjectUrl = imgObjectUrl
                photo.localThumbnaiObjectlUrl = imgThumbnailObjectUrl
              }
            }
            this.props.onSave(this.props.photo, takeAnotherPhoto)
          }
        )
      }
      return ''
    }

    async onDelete(): Promise<string> {
      const photo = this.props.photo
      if (isLocalImage(photo.url)) {
        await this.props.imageStorage.removeImage(photo.url)
      }
      if (photo.thumbnailUrl && isLocalImage(photo.thumbnailUrl)) {
        await this.props.imageStorage.removeImage(photo.thumbnailUrl)
      }
      if (photo.originalUrl && isLocalImage(photo.originalUrl)) {
        await this.props.imageStorage.removeImage(photo.originalUrl)
      }
      if (
        photo.originalThumbnailUrl &&
        isLocalImage(photo.originalThumbnailUrl)
      ) {
        await this.props.imageStorage.removeImage(photo.originalThumbnailUrl)
      }
      if (this.props.onDelete) {
        this.props.onDelete(this.props.photo)
      }
      return ''
    }

    onClose = (): void => {
      if (this.props.mode == 'new') {
        const photo = this.props.photo
        void this.props.imageStorage.removeImage(photo.url)
        this.props.imageStorage.removeImage(photo.thumbnailUrl)
        if (photo.originalUrl && isLocalImage(photo.originalUrl)) {
          void this.props.imageStorage.removeImage(photo.originalUrl)
        }
        if (
          photo.originalThumbnailUrl &&
          isLocalImage(photo.originalThumbnailUrl)
        ) {
          void this.props.imageStorage.removeImage(photo.originalThumbnailUrl)
        }
      }
      this.props.onClose()
    }
    render(): React.ReactNode {
      const photo = this.props.photo
      const localImage = isLocalImage(photo.url)
      const photoUrl = this.props.ctx.client.getAuthenticatedPhotoUrl(
        photo.localObjectUrl || photo.url
      )
      const maxPhotoHeight = getMaxPhotoHeight()
      const style: React.CSSProperties = {
        overflow: 'auto'
      }
      if (this.props.ctx.env.os == 'windows') {
        style.overflow = 'hidden'
      } else if (this.props.ctx.env.os == 'browser') {
        style.maxWidth = '450px'
      }

      style.maxHeight = maxPhotoHeight

      const isVideo =
        photo.contentType && photo.contentType.startsWith('video/')

      const photoTab = (
        <Card style={{ marginTop: 0 }}>
          <CardContent>
            {this.state.isAnnotating && this.state.annotationBase64 ? (
              <PhotoAnnotationEditor
                imageBase64={this.state.annotationBase64}
                originalImageBase64={
                  this.state.annotationOriginalBase64 || undefined
                }
                width={this.state.annotationWidth || undefined}
                onSave={this.onAnnotationSave}
                onCancel={this.onAnnotationCancel}
                saveLabel={this.props.ctx.i18next.t('save')}
                cancelLabel={this.props.ctx.i18next.t('cancel')}
                clearLabel={this.props.ctx.i18next.t('clear')}
              />
            ) : (
              <div>
                <div style={style}>
                  {isVideo ? (
                    <video
                      src={this.state.videoBlobUrl || photoUrl}
                      controls
                      playsInline
                      style={{
                        maxWidth: '100%',
                        maxHeight: maxPhotoHeight,
                        height: 'auto'
                      }}
                    />
                  ) : (
                    <img
                      ref={this.photoPreviewRef}
                      src={photoUrl}
                      style={{
                        maxWidth: '100%',
                        maxHeight: maxPhotoHeight,
                        height: 'auto'
                      }}
                    />
                  )}
                </div>
                <div>
                  <TextField
                    fullWidth
                    label={this.props.ctx.i18next?.t(
                      isVideo
                        ? 'initial_video_comment'
                        : 'initial_photo_comment'
                    )}
                    value={this.state.comment}
                    onChange={this.onCommentChanged}
                    disabled={
                      !localImage && !this.props.allowSavedPhotoCommentUpdate
                    }
                    margin='dense'
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )

      const mediaLabel = isVideo ? 'Video' : 'Photo'
      const tabs: ITabDefinition[] = [
        {
          name: mediaLabel,
          label: mediaLabel,
          component: photoTab
        }
      ]

      // only show comments tab for editing existing photo
      if (!localImage && this.props.isMultipleComment) {
        const commentTab = (
          <Card style={{ marginTop: 0 }}>
            <CardContent>
              <PhotoComments
                isOpen={true}
                photo={this.props.photo}
                fullName={this.props.fullName}
                dateTimeFormat={this.props.dateTimeFormat}
              />
            </CardContent>
          </Card>
        )
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const photoCount =
          this.props.photo && this.props.photo.comments
            ? this.props.photo.comments.filter((c1) => c1.action != 'delete')
                .length
            : 0
        tabs.push({
          name: 'Comments',
          label: 'Comments',
          component: commentTab,
          badgeContent: photoCount > 0 ? '' + photoCount : undefined
        })
      }
      const maxWidth = this.props.dialogMaxWidth
        ? this.props.dialogMaxWidth
        : this.state.dialogMaxWidth
      return (
        <Dialog
          disableBackdropClick={true}
          open={this.props.isOpen}
          onClose={this.onClose}
          aria-labelledby='form-dialog-title'
          // windows app disbale scrolling by default, we need to handle it by adding overflow:auto does scroll=paper work on windows?
          PaperProps={{
            style: {
              // added ENV.os == '' for resizing image in browser mode
              overflow:
                this.props.ctx.env.os == 'windows' ||
                this.props.ctx.env.os == 'android' ||
                this.props.ctx.env.os == ''
                  ? 'auto'
                  : this.props.ctx.env.os == 'browser' ||
                    this.props.ctx.env.os == 'ios'
                  ? 'overflow'
                  : 'hidden',
              // make sure the dialog doesn't resize during tab switching
              height:
                this.state.dialogHeight > 0 ? this.state.dialogHeight : '',
              width: this.state.dialogWidth > 0 ? this.state.dialogWidth : '',
              maxWidth: maxWidth
            }
          }}
        >
          <div
            id='photoDialog'
            style={{
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <DialogContent>
              <TabsComponent
                hasDetailsTab={false}
                color='light'
                compact={true}
                onClickTab={(): void => {
                  if (
                    this.state.dialogHeight == 0 ||
                    this.state.dialogWidth == 0
                  ) {
                    // first time click the comment tab, we need to save the dialog dimension
                    const dialogHeight =
                      document.getElementById('photoDialog') != null &&
                      document.getElementById('photoDialog')?.clientHeight
                        ? document.getElementById('photoDialog')!.clientHeight
                        : 0
                    const dialogWidth =
                      document.getElementById('photoDialog') != null &&
                      document.getElementById('photoDialog')?.clientWidth
                        ? document.getElementById('photoDialog')!.clientWidth
                        : 0
                    this.setState({ dialogHeight, dialogWidth })
                  }
                }}
                tabs={tabs}
              />
            </DialogContent>
            {!this.state.isAnnotating && (
              <DialogActions style={{ alignItems: 'flex-end' }}>
                {localImage && (
                  <Button color='primary' onClick={this.onClose}>
                    {this.props.ctx.i18next.t('cancel')}
                  </Button>
                )}
                {localImage && (
                  <Button color='primary' onClick={this.onStartAnnotation}>
                    {this.props.ctx.i18next.t('annotate')}
                  </Button>
                )}
                {(localImage || this.props.allowSavedPhotoDeletion) &&
                  this.props.mode == 'edit' && (
                    <Button style={{ color: '#F00' }} onClick={this.onDelete}>
                      {this.props.ctx.i18next.t('delete')}
                    </Button>
                  )}
                {(localImage || this.props.allowSavedPhotoCommentUpdate) && (
                  <Button color='primary' onClick={() => this.onSave(false)}>
                    {this.props.ctx.i18next.t('save')}
                  </Button>
                )}
                {this.props.showTakePhotoButton &&
                  localImage &&
                  photo.source == 'camera' &&
                  this.props.mode == 'new' && (
                    <Button color='primary' onClick={() => this.onSave(true)}>
                      {this.props.ctx.i18next.t('save_and_take_another_photo')}
                    </Button>
                  )}
                {!localImage && (
                  <>
                    <Button color='primary' onClick={this.onClose}>
                      {this.props.ctx.i18next.t('close')}
                    </Button>
                  </>
                )}
              </DialogActions>
            )}
          </div>
        </Dialog>
      )
    }
  }
)
